import React, { useState, useEffect, useCallback } from "react";
import style from "./PlatformsStyle";
import NewPlatform from "./NewPlatform/NewPlatform";
import {
  getAllEnterprises,
  getSubscriptionSummary,
} from "../../Services/enterpriseService";
import { useNavigate } from "react-router-dom"; // Importe o useNavigate

export default function Platforms() {
  const [enterprises, setEnterprises] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [sortOrder, setSortOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [view, setView] = useState("list");

  const navigate = useNavigate(); // Inicialize o hook

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statusValue =
        statusFilter === "Ativo" ? 2 : statusFilter === "Bloqueado" ? 3 : null;
      const params = {
        pageNumber: currentPage,
        pageSize: 10,
        searchFilter: searchTerm,
        status: statusValue,
        orderBy: orderBy,
        order: sortOrder,
      };

      const [enterprisesResponse, summaryData] = await Promise.all([
        getAllEnterprises(params),
        summary ? Promise.resolve(summary) : getSubscriptionSummary(),
      ]);

      setEnterprises(enterprisesResponse?.items || []);
      setTotalPages(enterprisesResponse?.totalPages || 0);

      if (summaryData && !summary) {
        setSummary(summaryData);
      }
    } catch (error) {
      console.error("Falha ao carregar os dados da página", error);
      setEnterprises([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, sortOrder, orderBy, summary]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const formatCurrency = (value) => {
    if (typeof value !== "number") return "R$ 0,00";
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const getStatusStyle = (status) => {
    if (status === 2) return style.statusAvailable;
    if (status === 3) return style.statusBlocked;
    return {};
  };

  const getStatusText = (status) => {
    if (status === 2) return "Ativo";
    if (status === 3) return "Bloqueado";
    return "Pendente";
  };

  // A função que navega para a página de detalhes
  const handleManageClick = (enterpriseId) => {
    navigate(`/platforms/${enterpriseId}`);
  };

  const handleShowList = () => {
    setView("list");
    fetchData();
  };

  if (loading && enterprises.length === 0) {
    return (
      <div
        style={{
          color: "white",
          textAlign: "center",
          padding: "50px",
          fontSize: "18px",
        }}
      >
        Carregando Clientes...
      </div>
    );
  }

  if (view === "new") {
    return <NewPlatform onFinish={handleShowList} />;
  }

  return (
    <div style={style.container}>
      <header style={style.header}>
        <div>
          <h1 style={style.title}>Clientes</h1>
          <p style={style.subtitle}>
            Gerencie seus clientes, assinaturas e status.
          </p>
        </div>
        <button style={style.addButton} onClick={() => setView("new")}>
          + Novo Cliente
        </button>
      </header>

      {summary && (
        <div style={style.statsGrid}>
          <div style={style.statCard}>
            <p style={style.statLabel}>Receita (Assinaturas Ativas)</p>
            <h2 style={style.statValue}>
              {formatCurrency(summary.totalValueActiveSubscriptions)}
            </h2>
          </div>
          <div style={style.statCard}>
            <p style={style.statLabel}>Clientes Ativos</p>
            <h2 style={style.statValue}>{summary.activeSubscriptionsCount}</h2>
          </div>
          <div style={style.statCard}>
            <p style={style.statLabel}>Pagamentos Pendentes</p>
            <h2 style={style.statValue}>
              {summary.pendingPaymentSubscriptionsCount}
            </h2>
          </div>
          <div style={style.statCard}>
            <p style={style.statLabel}>Clientes Bloqueados</p>
            <h2 style={style.statValue}>{summary.blockedSubscriptionsCount}</h2>
          </div>
        </div>
      )}

      <div style={style.listContainer}>
        <div style={style.filtersContainer}>
          <input
            type="search"
            placeholder="Pesquisar por nome ou CNPJ..."
            style={style.searchInput}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <div style={style.statusFilterGroup}>
            <button
              style={{
                ...style.statusButton,
                ...(statusFilter === "Todos" ? style.statusButtonActive : {}),
              }}
              onClick={() => handleStatusChange("Todos")}
            >
              Todos
            </button>
            <button
              style={{
                ...style.statusButton,
                ...(statusFilter === "Ativo" ? style.statusButtonActive : {}),
              }}
              onClick={() => handleStatusChange("Ativo")}
            >
              Ativos
            </button>
            <button
              style={{
                ...style.statusButton,
                ...(statusFilter === "Bloqueado"
                  ? style.statusButtonActive
                  : {}),
              }}
              onClick={() => handleStatusChange("Bloqueado")}
            >
              Bloqueados
            </button>
          </div>
        </div>

        <div style={style.platformList}>
          <div style={{ ...style.platformRow, ...style.listHeaderRow }}>
            <div style={{ ...style.platformCell, ...style.cellName }}>
              Cliente / Empresa
            </div>
            <div style={{ ...style.platformCell, ...style.cellContact }}>
              Contato Principal
            </div>
            <div style={{ ...style.platformCell, ...style.cellStatus }}>
              Status
            </div>
            <div style={{ ...style.platformCell, ...style.cellAction }}>
              Ações
            </div>
          </div>
          {loading ? (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#8B949E" }}
            >
              Atualizando...
            </div>
          ) : enterprises.length > 0 ? (
            enterprises.map((enterprise) => (
              <div key={enterprise.id} style={style.platformRow}>
                <div style={{ ...style.platformCell, ...style.cellName }}>
                  <div>
                    <h3 style={style.platformName}>{enterprise.name}</h3>
                    <p style={style.platformVersion}>CNPJ: {enterprise.cnpj}</p>
                  </div>
                </div>
                <div style={{ ...style.platformCell, ...style.cellContact }}>
                  {enterprise.contact}
                </div>
                <div style={{ ...style.platformCell, ...style.cellStatus }}>
                  <span
                    style={{
                      ...style.statusBadge,
                      ...getStatusStyle(enterprise.status),
                    }}
                  >
                    {getStatusText(enterprise.status)}
                  </span>
                </div>
                <div style={{ ...style.platformCell, ...style.cellAction }}>
                  <button
                    style={style.actionButton}
                    onClick={() => handleManageClick(enterprise.id)}
                  >
                    Gerenciar
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div style={{ ...style.platformRow, justifyContent: "center" }}>
              <p style={style.noResults}>Nenhum cliente encontrado.</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={style.paginationControls}>
            <button
              style={
                currentPage === 1 || loading
                  ? style.paginationButtonDisabled
                  : style.paginationButton
              }
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages}
            </span>
            <button
              style={
                currentPage === totalPages || loading
                  ? style.paginationButtonDisabled
                  : style.paginationButton
              }
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
